<template>
  <v-dialog v-model="dialog" persistent max-width="800">
    <AlFormCard title="Share Reports with Customer" width="100%">
      <template #error>
        <ErrorAlert v-if="error != null" :error="error" @clearErrors="clearErrors()" class="mb-2">
        </ErrorAlert>
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <v-card-text>
        <div class="d-flex align-center mb-6 px-2">
          <div class="text-subtitle-1">
            You are about to share {{ reports.length }} report(s) with customers. An email will be
            sent with secure access to these reports
          </div>
        </div>

        <div class="reports-container">
          <div v-for="(siteReports, site, siteIndex) in reportsBySite" :key="site" class="mb-5">
            <!-- Site header -->
            <div class="d-flex align-center mb-2 px-2">
              <v-icon icon="mdi-map-marker" color="primary" size="small" class="mr-2"></v-icon>
              <span class="text-h6 font-weight-medium">{{ site }}</span>
              <v-chip size="small" color="grey darken-1" variant="flat" class="ml-2">
                {{ siteReports.length }}
              </v-chip>
            </div>

            <!-- Report list -->
            <div class="report-list ml-5">
              <div
                v-for="report in siteReports"
                :key="report.id"
                class="report-item px-3 py-2 d-flex align-center"
              >
                <v-icon
                  icon="mdi-file-document-outline"
                  size="small"
                  class="mr-3 text-grey"
                ></v-icon>
                <div class="report-details flex-grow-1">
                  <div class="font-weight-medium">{{ report.report_id }}</div>
                  <div v-if="report.created" class="text-caption text-medium-emphasis">
                    Created {{ formatDate(report.created) }}
                  </div>
                </div>
              </div>
            </div>

            <v-divider
              class="mt-6"
              v-if="
                Object.keys(reportsBySite).length > 1 &&
                siteIndex != Object.keys(reportsBySite).length - 1
              "
            ></v-divider>
          </div>
        </div>
      </v-card-text>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="closeDialog()" :disabled="loading">Cancel</v-btn>
        <v-btn color="primary" variant="flat" @click="share" :loading="loading"> Share </v-btn>
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format } from 'date-fns'
import type { ISystemError } from '@/models/error'
import type { IReport } from '@/models/report'
import AlFormCard from '@/components/common/AlFormCard.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'

interface Props {
  reports: IReport[]
  loading?: boolean
}

const props = defineProps<Props>()
const dialog = defineModel<boolean>('dialog', { required: true })
const error = defineModel<ISystemError | null>('error', { default: null })

interface Emit {
  (e: 'share-reports'): void
  (e: 'cancel'): void
}
const emit = defineEmits<Emit>()

// Group reports by site
const reportsBySite = computed(() => {
  const groupedReports: Record<string, IReport[]> = {}

  props.reports.forEach((report) => {
    // Extract site name from the report
    const siteName = 'Site ' + report.site

    if (!groupedReports[siteName]) {
      groupedReports[siteName] = []
    }

    groupedReports[siteName].push(report)
  })

  return groupedReports
})

function formatDate(date: Date): string {
  return format(new Date(date), 'MMM dd, yyyy')
}

function clearErrors() {
  error.value = null
}

function closeDialog() {
  clearErrors()
  dialog.value = false
}

function share() {
  emit('share-reports')
}
</script>

<style scoped>
.v-list-item {
  min-height: 40px !important;
}

.reports-container {
  max-height: 450px;
  overflow-y: auto;
}

.report-list {
  border-radius: 8px;
}

.report-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.report-item:last-child {
  border-bottom: none;
}

.report-item:hover {
  background-color: #eeeef2;
}
</style>
